'use client';

import { useEffect, useRef, useState } from 'react';
import { buildPaymentDescription } from '@kit/payments/description';

import { useRouter, useSearchParams } from 'next/navigation';

import { useTranslation } from 'react-i18next';

import type { CheckoutResult, ProviderName } from '@kit/payments';
import { toast } from '@kit/ui/sonner';

import { TenantBeginCheckout } from '~/(tenant)/_components/analytics/tenant-begin-checkout';
import { TenantPurchaseTracker } from '~/(tenant)/_components/analytics/tenant-purchase-tracker';
import type { TenantVerifiedPurchase } from '~/(tenant)/_lib/analytics/tenant-purchase.types';
import { HyperpayCheckoutDialog } from '~/(tenant)/book/_components/hyperpay-checkout-dialog';
import { MoyasarCheckoutDialog } from '~/(tenant)/book/_components/moyasar-checkout-dialog';
import { StripeCheckoutForm } from '~/(tenant)/book/_components/stripe-checkout-form';
import { ApplePayMembershipPurchaseButton } from '~/(tenant)/payment/_components/apple-pay-membership-purchase-button';
import {
  type PublicDiscountCode,
  publicCodesForMembership,
} from '~/(tenant)/book/_lib/public-discount-codes';
import {
  createMembershipPurchaseCheckoutAction,
  purchaseMembershipWithWalletAction,
} from '~/(tenant)/book/_lib/server/memberships/membership-purchase-actions';
import { confirmMembershipPurchaseReturnAction } from '~/(tenant)/book/_lib/server/memberships/membership-purchase-return-actions';

import { canPayMembershipWithWallet } from '../_lib/membership-wallet';
import type { AvailableMembershipPlan } from '../_lib/server/available-memberships.loader';
import {
  MembershipCheckoutReview,
  type ReviewSelection,
} from './membership-checkout-review';
import { MembershipRateCards } from './membership-rate-cards';

interface CheckoutState {
  plan: AvailableMembershipPlan;
  /** What the buyer is charged: the plan price less any discount code. */
  price: number;
  discountMetadata: Record<string, string> | null;
  provider: ProviderName;
  publishableKey?: string;
  tenantId?: string;
  tenantName?: string;
  locationId?: string;
  userId?: string;
  purchaseId: string;
  checkout?: CheckoutResult;
}

/**
 * Self-serve membership catalog + atomic checkout host. Mirrors how the package
 * purchase flow drives Moyasar / HyperPay / Stripe dialogs from one place. For
 * self-serve plans (requires_approval === false) it launches checkout in place;
 * approval plans render the plain card (which links to the intake form).
 *
 * The membership row is materialized only on payment confirm — this component
 * never writes one. On return (Moyasar `?id=`, HyperPay `?resourcePath=`) it
 * polls the confirm action and refreshes the page so the new active membership
 * surfaces in the "Active" tab.
 */
export function SelfServeCatalog({
  plans,
  isAuthenticated,
  wallet,
  tenantId,
  publicDiscountCodes,
}: {
  plans: AvailableMembershipPlan[];
  isAuthenticated: boolean;
  wallet: { enabled: boolean; balance: number };
  tenantId: string;
  publicDiscountCodes: PublicDiscountCode[];
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const searchParams = useSearchParams();

  const [purchaseInFlight, setPurchaseInFlight] = useState<{
    planId: string;
    method: 'provider' | 'wallet';
    /** What the buyer is about to be charged, after any discount code. */
    total: number;
  } | null>(null);
  // The plan being reviewed before payment, where a discount code is entered.
  const [reviewPlan, setReviewPlan] = useState<AvailableMembershipPlan | null>(
    null,
  );
  const [checkoutState, setCheckoutState] = useState<CheckoutState | null>(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifiedPurchase, setVerifiedPurchase] =
    useState<TenantVerifiedPurchase | null>(null);
  // Apple Pay availability, resolved once on the client. ApplePaySession is a
  // Safari-only browser API (absent during SSR), and canMakePayments() may
  // throw on managed devices. Safe as a lazy initializer: nothing gated on it
  // renders until the buyer opens a checkout dialog, so there is no hydration
  // mismatch surface.
  const [applePayAvailable] = useState(() => {
    type WindowWithApplePay = Window & {
      ApplePaySession?: { canMakePayments(): boolean };
    };

    if (typeof window === 'undefined') return false;

    try {
      return (
        (window as WindowWithApplePay).ApplePaySession?.canMakePayments() ??
        false
      );
    } catch {
      return false;
    }
  });
  const processedRef = useRef<string | null>(null);

  // Return-page confirm. Moyasar appends ?id=<paymentId>; HyperPay appends
  // ?provider=hyperpay&resourcePath=… . Both land back on
  // /memberships?membership_purchase=1.
  useEffect(() => {
    if (searchParams.get('membership_purchase') !== '1') return;

    const moyasarId = searchParams.get('id');
    const resourcePath = searchParams.get('resourcePath');
    const isHyperpay = searchParams.get('provider') === 'hyperpay';
    const key = moyasarId ?? resourcePath ?? null;

    if (!key || isVerifying || processedRef.current === key) return;

    // Dedup guard set synchronously; the verifying flag is flipped inside the
    // async task (off the effect body) so we don't setState synchronously here.
    processedRef.current = key;

    const clearQuery = () => {
      window.history.replaceState({}, '', '/memberships');
    };

    void (async () => {
      setIsVerifying(true);
      try {
        const result = await confirmMembershipPurchaseReturnAction(
          isHyperpay && resourcePath
            ? { resourcePath }
            : { paymentId: moyasarId! },
        );

        if (result.success && result.status === 'confirmed') {
          clearQuery();
          setVerifiedPurchase(result.purchase);
          toast.success(
            t('common:memberClub.memberships.purchase.toastSuccess', {
              defaultValue: 'Membership activated!',
            }),
          );
          setIsVerifying(false);
          router.refresh();
          return;
        }

        if (result.status === 'underpaid') {
          clearQuery();
          toast.warning(
            t('common:memberClub.memberships.purchase.toastUnderpaid', {
              defaultValue:
                'Your payment is less than the membership price, so the membership is not active yet. The venue will contact you to settle the balance.',
            }),
          );
          setIsVerifying(false);
          router.refresh();
          return;
        }

        if (result.status === 'failed' || result.status === 'error') {
          clearQuery();
          toast.error(
            result.error ||
              t('common:memberClub.memberships.purchase.toastFailed', {
                defaultValue: 'Payment failed. Please try again.',
              }),
          );
          setIsVerifying(false);
          return;
        }

        // pending — provider still processing. Tell the user and stop.
        clearQuery();
        toast.info(
          t('common:memberClub.memberships.purchase.toastProcessing', {
            defaultValue:
              'Payment is processing — your membership will appear shortly.',
          }),
        );
        setIsVerifying(false);
      } catch {
        clearQuery();
        toast.error(
          t('common:memberClub.memberships.purchase.toastVerifyFailed', {
            defaultValue:
              'Could not verify payment. Please check your memberships.',
          }),
        );
        setIsVerifying(false);
      }
    })();
  }, [searchParams, isVerifying, router, t]);

  const openReview = (plan: AvailableMembershipPlan) => {
    // Purchasing requires an account to own the membership.
    if (!isAuthenticated) {
      const next = encodeURIComponent('/memberships');
      router.push(`/auth/sign-in?next=${next}`);
      return;
    }

    if (!purchaseInFlight) setReviewPlan(plan);
  };

  const purchaseErrorMessage = (code: string) =>
    t(`common:memberClub.memberships.purchase.errors.${code}`, {
      defaultValue: t('common:memberClub.memberships.purchase.errors.generic'),
    });

  const handlePurchase = async (
    plan: AvailableMembershipPlan,
    { discountCode, total }: ReviewSelection,
  ) => {
    // One checkout at a time: a second click on another card while the first
    // checkout action is in flight would race, and the later resolution could
    // swap checkoutState under an already-mounted dialog.
    if (purchaseInFlight) {
      return;
    }

    setPurchaseInFlight({ planId: plan.id, method: 'provider', total });
    try {
      const baseUrl = window.location.origin;
      const result = await createMembershipPurchaseCheckoutAction({
        planId: plan.id,
        returnUrl: `${baseUrl}/memberships?membership_purchase=1`,
        price: Number(plan.price),
        discountCode,
      });

      if (!result.success) {
        toast.error(purchaseErrorMessage(result.error));
        setPurchaseInFlight(null);
        return;
      }

      setReviewPlan(null);

      // Free plan or a full discount — activated instantly server-side.
      if (result.data.free) {
        toast.success(
          t('common:memberClub.memberships.purchase.toastSuccess', {
            defaultValue: 'Membership activated!',
          }),
        );
        setPurchaseInFlight(null);
        router.refresh();
        return;
      }

      const checkoutResult = result.data.checkout as CheckoutResult | undefined;

      // Redirect-mode providers (Stripe hosted, Ziina redirect, …).
      if (checkoutResult?.mode === 'redirect') {
        window.location.href = checkoutResult.redirectUrl;
        return;
      }

      setCheckoutState({
        plan,
        price: result.data.price,
        discountMetadata: result.data.discountMetadata,
        provider: result.data.provider as ProviderName,
        publishableKey: result.data.publishableKey ?? undefined,
        tenantId: result.data.tenantId ?? undefined,
        tenantName: result.data.tenantName ?? undefined,
        locationId: result.data.locationId ?? undefined,
        userId: result.data.userId ?? undefined,
        purchaseId: result.data.purchaseId,
        checkout: checkoutResult,
      });
      setPurchaseInFlight(null);
      setShowCheckout(true);
    } catch {
      toast.error(
        t('common:memberClub.memberships.purchase.toastUnexpected', {
          defaultValue: 'Something went wrong. Please try again.',
        }),
      );
      setPurchaseInFlight(null);
    }
  };

  const handleWalletPurchase = async (
    plan: AvailableMembershipPlan,
    { discountCode, total }: ReviewSelection,
  ) => {
    if (!isAuthenticated || purchaseInFlight) return;

    setPurchaseInFlight({ planId: plan.id, method: 'wallet', total });
    try {
      const result = await purchaseMembershipWithWalletAction({
        planId: plan.id,
        discountCode,
      });

      if (!result.success) {
        toast.error(purchaseErrorMessage(result.error));
        setPurchaseInFlight(null);
        return;
      }

      toast.success(t(
        'common:memberClub.memberships.purchase.toastWalletSuccess',
        { defaultValue: 'Membership activated and paid from your wallet!' },
      ));
      router.push(`/memberships/${result.data.membership_id}`);
    } catch {
      toast.error(t(
        'common:memberClub.memberships.purchase.toastWalletFailed',
        { defaultValue: 'Could not pay with your wallet. Please try again.' },
      ));
      setPurchaseInFlight(null);
    }
  };

  const closeCheckout = () => {
    setShowCheckout(false);
    setCheckoutState(null);
  };

  const purchasingPlan = plans.find(
    (candidate) => candidate.id === purchaseInFlight?.planId,
  );

  return (
    <>
      <TenantPurchaseTracker purchase={verifiedPurchase} />
      {purchasingPlan && purchaseInFlight && purchaseInFlight.total > 0 ? (
        <TenantBeginCheckout
          contentId={purchasingPlan.id}
          contentName={purchasingPlan.name}
          contentType="membership_purchase"
          value={purchaseInFlight.total}
        />
      ) : null}
      {isVerifying && (
        <div className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
          <div className="border-primary h-12 w-12 animate-spin rounded-full border-4 border-t-transparent" />
        </div>
      )}

      <MembershipRateCards
        plans={plans}
        cardProps={(plan) => ({
          onPurchase: openReview,
          onWalletPurchase: canPayMembershipWithWallet({
            isAuthenticated,
            isAuthorizedPayer: true,
            walletEnabled: wallet.enabled,
            walletBalance: wallet.balance,
            amount: Number(plan.price),
          }) ? openReview : undefined,
          walletBalance: wallet.balance,
          isPurchasing: purchaseInFlight?.planId === plan.id,
        })}
      />

      {reviewPlan && (
        <MembershipCheckoutReview
          key={reviewPlan.id}
          plan={reviewPlan}
          tenantId={tenantId}
          wallet={wallet}
          suggestedCodes={publicCodesForMembership(
            publicDiscountCodes,
            reviewPlan,
          ).map((code) => ({
            code: code.code,
            name: code.name,
            discountType: code.discount_type,
            discountValue: Number(code.discount_value),
          }))}
          pending={purchaseInFlight !== null}
          onClose={() => setReviewPlan(null)}
          onPay={(selection) => handlePurchase(reviewPlan, selection)}
          onPayWithWallet={(selection) =>
            handleWalletPurchase(reviewPlan, selection)
          }
        />
      )}

      {showCheckout && checkoutState?.provider === 'moyasar' && (
        <MoyasarCheckoutDialog
          // Keyed on the purchase so a changed checkoutState remounts the
          // dialog (fresh card form + fresh Apple Pay closure) instead of
          // reconciling a different purchase into mounted payment UI.
          key={checkoutState.purchaseId}
          isOpen={showCheckout}
          checkoutData={{
            sessionId: checkoutState.purchaseId,
            provider: 'moyasar',
            isMembershipPurchase: true,
            membershipPurchaseId: checkoutState.purchaseId,
            membershipPlanId: checkoutState.plan.id,
            membershipPeriod: checkoutState.plan.period,
            // Stamped into Moyasar metadata.user_id — required by the
            // confirm/webhook paths to materialize the membership.
            userId: checkoutState.userId,
            publishableKey: checkoutState.publishableKey,
            tenantId: checkoutState.tenantId,
            tenantName: checkoutState.tenantName,
            locationId: checkoutState.locationId,
            extraMetadata: checkoutState.discountMetadata ?? undefined,
          }}
          bookingDetails={{
            amount: checkoutState.price,
            description: buildPaymentDescription({
              surface: 'membership_purchase',
              name: checkoutState.plan.name,
            }),
          }}
          returnUrl={`${typeof window !== 'undefined' ? window.location.origin : ''}/memberships?membership_purchase=1`}
          applePaySlot={
            applePayAvailable &&
            checkoutState.publishableKey &&
            checkoutState.userId ? (
              <ApplePayMembershipPurchaseButton
                plan={{
                  id: checkoutState.plan.id,
                  name: checkoutState.plan.name,
                  price: checkoutState.price,
                  period: checkoutState.plan.period,
                }}
                extraMetadata={checkoutState.discountMetadata}
                purchaseId={checkoutState.purchaseId}
                publishableKey={checkoutState.publishableKey}
                userId={checkoutState.userId}
                tenantId={checkoutState.tenantId}
                tenantName={checkoutState.tenantName}
                locationId={checkoutState.locationId}
                // Close only on a successful charge — cancel/failure keeps the
                // dialog open so the buyer can retry or fall back to card.
                onPaymentSuccess={closeCheckout}
              />
            ) : undefined
          }
          onClose={closeCheckout}
          onSuccess={closeCheckout}
        />
      )}

      {showCheckout &&
        checkoutState?.provider === 'hyperpay' &&
        checkoutState.checkout?.mode === 'embedded_widget' &&
        'checkoutId' in checkoutState.checkout && (
          <HyperpayCheckoutDialog
            isOpen={showCheckout}
            checkoutData={{
              sessionId: checkoutState.checkout.checkoutId,
              integrity:
                'integrity' in checkoutState.checkout
                  ? (checkoutState.checkout.integrity as string | undefined)
                  : undefined,
              provider: 'hyperpay',
            }}
            returnPath={`/memberships?membership_purchase=1`}
            onClose={closeCheckout}
            onSuccess={closeCheckout}
          />
        )}

      {showCheckout &&
        checkoutState?.checkout?.mode === 'embedded_stripe' &&
        'clientSecret' in checkoutState.checkout && (
          <StripeCheckoutForm
            isOpen={showCheckout}
            onClose={closeCheckout}
            clientSecret={checkoutState.checkout.clientSecret}
            publishableKey={checkoutState.checkout.publishableKey}
          />
        )}
    </>
  );
}
